import { useEffect, useRef, useMemo, useCallback } from "react";
import {
  Scene,
  OrthographicCamera,
  WebGLRenderer,
  PlaneGeometry,
  ShaderMaterial,
  Mesh,
  Vector2,
  Vector3,
  Color,
} from "three";

import "./PixelSnow.css";

const vertexShader = `
void main() {
  gl_Position = vec4(position, 1.0);
}
`;

const fragmentShader = `
precision mediump float;

uniform float uTime;
uniform vec2 uResolution;
uniform float uFlakeSize;
uniform float uMinFlakeSize;
uniform float uPixelResolution;
uniform float uSpeed;
uniform float uDepthFade;
uniform float uFarPlane;
uniform vec3 uColor;
uniform float uBrightness;
uniform float uGamma;
uniform float uDensity;
uniform float uVariant;
uniform float uDirection;

// --- constants ---
#define PI 3.14159265
#define PI_OVER_6 0.5235988
#define PI_OVER_3 1.0471976
#define M1 1597334677U
#define M2 3812015801U
#define M3 3299493293U
#define F0 2.3283064e-10

// Max ray-march steps — reduced from 128 for perf. 64 covers farPlane ≤ 14
// with plenty of headroom.  Use 80 if you push farPlane beyond 14.
#define MAX_STEPS 64

#define hash(n) (n * (n ^ (n >> 15)))
#define coord3(p) (uvec3(p).x * M1 ^ uvec3(p).y * M2 ^ uvec3(p).z * M3)

// Isometric camera basis (precomputed, constant)
const vec3 camK = vec3(0.57735027, 0.57735027, 0.57735027);
const vec3 camI = vec3(0.70710678, 0.0, -0.70710678);
const vec3 camJ = vec3(-0.40824829, 0.81649658, -0.40824829);

// Branch direction for snowflake SDF
const vec2 b1d = vec2(0.574, 0.819);

// ---- cheap hash producing 4 floats (xyz + w) from one seed ----
vec4 hash4(uint n) {
  uvec4 hashed = hash(n) * uvec4(1U, 511U, 262143U, 134217727U);
  return vec4(hashed) * F0;
}

// ---- snowflake SDF (6-fold symmetric crystal) ----
float snowflakeDist(vec2 p) {
  float r = length(p);
  float a = atan(p.y, p.x);
  a = abs(mod(a + PI_OVER_6, PI_OVER_3) - PI_OVER_6);
  vec2 q = r * vec2(cos(a), sin(a));
  float dMain = max(abs(q.y), max(-q.x, q.x - 1.0));
  float b1t = clamp(dot(q - vec2(0.4, 0.0), b1d), 0.0, 0.4);
  float dB1 = length(q - vec2(0.4, 0.0) - b1t * b1d);
  float b2t = clamp(dot(q - vec2(0.7, 0.0), b1d), 0.0, 0.25);
  float dB2 = length(q - vec2(0.7, 0.0) - b2t * b1d);
  return min(dMain, min(dB1, dB2)) * 10.0;
}

void main() {
  // ---- pixelise ----
  float invPixelRes = 1.0 / uPixelResolution;
  float pixelSize   = max(1.0, floor(0.5 + uResolution.x * invPixelRes));
  float invPixelSize = 1.0 / pixelSize;

  vec2 fragCoord = floor(gl_FragCoord.xy * invPixelSize);
  vec2 res       = uResolution * invPixelSize;
  float invResX  = 1.0 / res.x;

  vec3 ray = normalize(vec3((fragCoord - res * 0.5) * invResX, 1.0));
  ray = ray.x * camI + ray.y * camJ + ray.z * camK;

  // ---- camera motion ----
  float timeSpeed = uTime * uSpeed;
  float windX = cos(uDirection) * 0.4;
  float windY = sin(uDirection) * 0.4;
  vec3 camPos = (windX * camI + windY * camJ + 0.1 * camK) * timeSpeed;
  vec3 pos    = camPos;

  // ---- DDA setup ----
  vec3 absRay  = max(abs(ray), vec3(0.001));
  vec3 strides = 1.0 / absRay;
  vec3 raySign = step(ray, vec3(0.0));
  vec3 phase   = fract(pos) * strides;
  phase = mix(strides - phase, phase, raySign);

  // ---- precomputes ----
  float rayDotCamK    = dot(ray, camK);
  float invRayDotCamK = 1.0 / rayDotCamK;
  float invDepthFade  = 1.0 / uDepthFade;
  float halfInvResX   = 0.5 * invResX;

  // Simplified wobble animation: one sin per axis instead of cos(sin+sin).
  // Moves flakes smoothly with ~1/3 the ALU of the old path.
  vec3 wobble = vec3(
    sin(timeSpeed * 0.7),
    sin(timeSpeed * 0.8 + 1.5),
    sin(timeSpeed * 0.5 + 3.0)
  ) * 0.35;

  // Accumulator for compositing multiple flake layers (back-to-front).
  vec3  accum  = vec3(0.0);
  float accumA = 0.0;

  float t = 0.0;
  for (int i = 0; i < MAX_STEPS; i++) {
    if (t >= uFarPlane) break;
    // Early-out: once accumulated alpha is nearly opaque, stop.
    if (accumA > 0.97) break;

    vec3 fpos = floor(pos);
    uint cellCoord = coord3(fpos);
    vec4 h = hash4(cellCoord);

    if (h.x < uDensity) {
      // ---- flake position (cheap wobble) ----
      vec3 flakeOff = fract(h.xyz * vec3(17.31, 13.73, 23.17));
      vec3 flakePos = fpos + mix(vec3(0.15), vec3(0.85), flakeOff) + wobble * flakeOff;

      float toIntersection = dot(flakePos - pos, camK) * invRayDotCamK;

      if (toIntersection > 0.0) {
        vec3  testPos = pos + ray * toIntersection - flakePos;
        float testX   = dot(testPos, camI);
        float testY   = dot(testPos, camJ);
        vec2  testUV  = abs(vec2(testX, testY));

        float depth    = dot(flakePos - camPos, camK);
        // Per-flake random size variation (0.7x – 1.3x) for organic feel
        float sizeJitter = 0.7 + 0.6 * h.w;
        float flakeSize  = max(uFlakeSize * sizeJitter,
                               uMinFlakeSize * depth * halfInvResX);

        // ---- shape test ----
        float dist;
        if (uVariant < 0.5) {
          dist = max(testUV.x, testUV.y);
        } else if (uVariant < 1.5) {
          dist = length(testUV);
        } else {
          float invFS = 1.0 / flakeSize;
          dist = snowflakeDist(vec2(testX, testY) * invFS) * flakeSize;
        }

        if (dist < flakeSize) {
          // ---- soft edge alpha (smooth fall-off at rim) ----
          float edge = 1.0 - smoothstep(flakeSize * 0.3, flakeSize, dist);

          float flakeSizeRatio = uFlakeSize / flakeSize;
          float depthAtten     = exp2(-(t + toIntersection) * invDepthFade);
          float intensity      = depthAtten
                                 * min(1.0, flakeSizeRatio * flakeSizeRatio)
                                 * uBrightness;

          float value = clamp(pow(intensity, uGamma), 0.0, 1.0);

          // Skip invisible flakes
          if (value > 0.015) {
            // Depth-based cool tint: distant flakes shift slightly blue
            float coolShift = smoothstep(0.0, uFarPlane, depth);
            vec3  flakeCol  = mix(uColor, uColor * vec3(0.82, 0.88, 1.0), coolShift * 0.45);

            // Subtle glow halo around bright nearby flakes
            float glow   = exp(-dist * dist / (flakeSize * flakeSize * 0.6)) * depthAtten * 0.35;
            flakeCol    += uColor * glow;

            float alpha = value * edge;
            // Front-to-back compositing
            accum  += flakeCol * alpha * (1.0 - accumA);
            accumA += alpha * (1.0 - accumA);
          }
        }
      }
    }

    // ---- advance DDA ----
    float nextStep = min(min(phase.x, phase.y), phase.z);
    vec3 sel = step(phase, vec3(nextStep));
    phase = phase - nextStep + strides * sel;
    t    += nextStep;
    pos   = mix(pos + ray * nextStep, floor(pos + ray * nextStep + 0.5), sel);
  }

  gl_FragColor = vec4(accum, accumA);
}
`;

export default function PixelSnow({
  color = "#ffffff",
  flakeSize = 0.01,
  minFlakeSize = 1.25,
  pixelResolution = 200,
  maxDevicePixelRatio = 2,
  speed = 1.25,
  depthFade = 4,
  farPlane = 12,
  brightness = 1,
  gamma = 0.4545,
  density = 0.3,
  variant = "square",
  direction = 125,
  className = "",
  style = {},
}) {
  const containerRef = useRef(null);
  const animationRef = useRef(0);
  const isVisibleRef = useRef(true);
  const isFocusedRef = useRef(true);
  const rendererRef = useRef(null);
  const materialRef = useRef(null);
  const resizeTimeoutRef = useRef(null);
  const lastFrameRef = useRef(0);

  // Memoize shader variant value
  const variantValue = useMemo(() => {
    return variant === "round" ? 1.0 : variant === "snowflake" ? 2.0 : 0.0;
  }, [variant]);

  // Memoize color conversion
  const colorVector = useMemo(() => {
    const threeColor = new Color(color);
    return new Vector3(threeColor.r, threeColor.g, threeColor.b);
  }, [color]);

  // Debounced resize handler
  const handleResize = useCallback(() => {
    if (resizeTimeoutRef.current) {
      clearTimeout(resizeTimeoutRef.current);
    }
    resizeTimeoutRef.current = window.setTimeout(() => {
      const container = containerRef.current;
      const renderer = rendererRef.current;
      const material = materialRef.current;
      if (!container || !renderer || !material) return;

      const w = container.offsetWidth;
      const h = container.offsetHeight;
      renderer.setSize(w, h);
      const pixelRatio = renderer.getPixelRatio();
      material.uniforms.uResolution.value.set(w * pixelRatio, h * pixelRatio);
    }, 100);
  }, []);

  // Visibility observer
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        isVisibleRef.current = entry.isIntersecting;
      },
      { threshold: 0 },
    );

    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  // Focus listener — throttle when tab loses focus
  useEffect(() => {
    const onFocus = () => {
      isFocusedRef.current = true;
    };
    const onBlur = () => {
      isFocusedRef.current = false;
    };
    window.addEventListener("focus", onFocus);
    window.addEventListener("blur", onBlur);
    isFocusedRef.current = document.hasFocus();
    return () => {
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("blur", onBlur);
    };
  }, []);

  // Main Three.js setup - only runs once
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const scene = new Scene();
    const camera = new OrthographicCamera(-1, 1, 1, -1, 0, 1);
    const renderer = new WebGLRenderer({
      antialias: false,
      alpha: true,
      premultipliedAlpha: false,
      powerPreference: "high-performance",
      stencil: false,
      depth: false,
    });

    const pixelRatio = Math.min(
      window.devicePixelRatio || 1,
      maxDevicePixelRatio,
    );
    renderer.setPixelRatio(pixelRatio);
    renderer.setSize(container.offsetWidth, container.offsetHeight);
    renderer.setClearColor(0x000000, 0);
    container.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    const material = new ShaderMaterial({
      vertexShader,
      fragmentShader,
      uniforms: {
        uTime: { value: 0 },
        uResolution: {
          value: new Vector2(
            container.offsetWidth * pixelRatio,
            container.offsetHeight * pixelRatio,
          ),
        },
        uFlakeSize: { value: flakeSize },
        uMinFlakeSize: { value: minFlakeSize },
        uPixelResolution: { value: pixelResolution },
        uSpeed: { value: speed },
        uDepthFade: { value: depthFade },
        uFarPlane: { value: farPlane },
        uColor: { value: colorVector.clone() },
        uBrightness: { value: brightness },
        uGamma: { value: gamma },
        uDensity: { value: density },
        uVariant: { value: variantValue },
        uDirection: { value: (direction * Math.PI) / 180 },
      },
      transparent: true,
    });
    materialRef.current = material;

    const geometry = new PlaneGeometry(2, 2);
    scene.add(new Mesh(geometry, material));

    window.addEventListener("resize", handleResize);

    const startTime = performance.now();
    const animate = (now) => {
      animationRef.current = requestAnimationFrame(animate);

      // Only render if visible
      if (!isVisibleRef.current) return;

      // Throttle to ~20 fps when tab is not focused
      if (!isFocusedRef.current) {
        if (now - lastFrameRef.current < 50) return;
      }
      lastFrameRef.current = now;

      material.uniforms.uTime.value = (now - startTime) * 0.001;
      renderer.render(scene, camera);
    };
    animationRef.current = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(animationRef.current);
      window.removeEventListener("resize", handleResize);
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
      if (container.contains(renderer.domElement)) {
        container.removeChild(renderer.domElement);
      }
      renderer.dispose();
      geometry.dispose();
      material.dispose();
      rendererRef.current = null;
      materialRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleResize]); // Only recreate scene when handleResize changes

  useEffect(() => {
    const container = containerRef.current;
    const renderer = rendererRef.current;
    const material = materialRef.current;
    if (!container || !renderer || !material) return;

    const pixelRatio = Math.min(
      window.devicePixelRatio || 1,
      maxDevicePixelRatio,
    );
    renderer.setPixelRatio(pixelRatio);
    renderer.setSize(container.offsetWidth, container.offsetHeight);
    material.uniforms.uResolution.value.set(
      container.offsetWidth * pixelRatio,
      container.offsetHeight * pixelRatio,
    );
  }, [maxDevicePixelRatio]);

  // Update material uniforms when props change
  useEffect(() => {
    const material = materialRef.current;
    if (!material) return;

    material.uniforms.uFlakeSize.value = flakeSize;
    material.uniforms.uMinFlakeSize.value = minFlakeSize;
    material.uniforms.uPixelResolution.value = pixelResolution;
    material.uniforms.uSpeed.value = speed;
    material.uniforms.uDepthFade.value = depthFade;
    material.uniforms.uFarPlane.value = farPlane;
    material.uniforms.uBrightness.value = brightness;
    material.uniforms.uGamma.value = gamma;
    material.uniforms.uDensity.value = density;
    material.uniforms.uVariant.value = variantValue;
    material.uniforms.uDirection.value = (direction * Math.PI) / 180;
    material.uniforms.uColor.value.copy(colorVector);
  }, [
    flakeSize,
    minFlakeSize,
    pixelResolution,
    speed,
    depthFade,
    farPlane,
    brightness,
    gamma,
    density,
    variantValue,
    direction,
    colorVector,
  ]);

  return (
    <div
      ref={containerRef}
      className={`pixel-snow-container ${className}`}
      style={style}
    />
  );
}
